import { ArrowUpRight, Facebook, Instagram } from "lucide-react";
import {
    ADDRESS_1,
    ADDRESS_2,
    CITY,
    EMAIL,
    EMAIL_MAILTO,
    FACEBOOK_URL,
    HOURS,
    INSTAGRAM_URL,
    MAP_EMBED,
    MAPS_URL,
    PHONE2_SHORT,
    PHONE2_TEL,
    PHONE_SHORT,
    PHONE_TEL,
    WHATSAPP_URL,
} from "../../data/site";
import { Reveal, SectionMark } from "./Reveal";

export const Location = () => (
    <section
        id="kontakt"
        data-testid="location-section"
        className="relative border-t border-white/[0.07] px-5 py-28 md:px-10 md:py-40"
    >
        <div className="mx-auto max-w-[1400px]">
            <Reveal>
                <SectionMark>( 09 — Lokacija )</SectionMark>
            </Reveal>
            <Reveal delay={0.1}>
                <h2 className="mt-8 font-display text-[9.5vw] font-light leading-[0.98] tracking-tight text-bone md:text-[5.5vw]">
                    YOUR CAR.
                    <br />
                    <span className="italic text-bronze">OUR CRAFT.</span>
                </h2>
            </Reveal>

            <div className="mt-16 grid gap-14 md:mt-24 md:grid-cols-2 md:gap-16">
                <div>
                    <Reveal>
                        <p className="font-display text-2xl font-light text-bone md:text-3xl">
                            {CITY}
                        </p>
                        <p className="mt-4 text-sm leading-relaxed text-silver">
                            {ADDRESS_1}
                            <br />
                            {ADDRESS_2}
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <a
                            href={PHONE_TEL}
                            data-testid="location-phone-link"
                            className="mt-9 inline-block font-display text-4xl font-light tracking-tight text-bone transition-colors duration-300 hover:text-bronze md:text-6xl"
                        >
                            {PHONE_SHORT}
                        </a>
                        <a
                            href={PHONE2_TEL}
                            data-testid="location-phone2-link"
                            className="mt-4 block font-display text-4xl font-light tracking-tight text-bone transition-colors duration-300 hover:text-bronze md:text-6xl"
                        >
                            {PHONE2_SHORT}
                        </a>
                    </Reveal>

                    <Reveal delay={0.14}>
                        <a
                            href={EMAIL_MAILTO}
                            data-testid="location-email-link"
                            className="mt-5 inline-block border-b border-bone/25 pb-1 text-sm tracking-[0.12em] text-bone/80 transition-colors duration-300 hover:border-bronze hover:text-bronze"
                        >
                            {EMAIL}
                        </a>
                    </Reveal>

                    <Reveal delay={0.16}>
                        <div className="mt-7 flex gap-3">
                            <a
                                href={FACEBOOK_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-facebook"
                                aria-label="Facebook profil"
                                className="flex h-11 w-11 items-center justify-center border border-white/15 text-silver transition-colors duration-300 hover:border-bronze hover:text-bronze"
                            >
                                <Facebook size={17} strokeWidth={1.5} />
                            </a>
                            <a
                                href={INSTAGRAM_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="contact-instagram"
                                aria-label="Instagram profil"
                                className="flex h-11 w-11 items-center justify-center border border-white/15 text-silver transition-colors duration-300 hover:border-bronze hover:text-bronze"
                            >
                                <Instagram size={17} strokeWidth={1.5} />
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={0.18} className="mt-10 flex flex-wrap gap-4">
                        <a
                            href={PHONE_TEL}
                            data-testid="location-call-button"
                            className="bg-bone px-9 py-4 text-[11px] font-semibold tracking-[0.26em] text-[#0a0a0b] transition-colors duration-300 hover:bg-bronze"
                        >
                            POZOVI
                        </a>
                        <a
                            href={MAPS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="location-maps-button"
                            className="inline-flex items-center gap-2 border border-bone/25 px-9 py-4 text-[11px] font-semibold tracking-[0.26em] text-bone transition-colors duration-300 hover:border-bronze hover:text-bronze"
                        >
                            GOOGLE MAPS
                            <ArrowUpRight size={14} strokeWidth={1.5} />
                        </a>
                        <a
                            href={WHATSAPP_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="location-whatsapp-button"
                            className="inline-flex items-center gap-2 border border-bone/25 px-9 py-4 text-[11px] font-semibold tracking-[0.26em] text-bone transition-colors duration-300 hover:border-bronze hover:text-bronze"
                        >
                            WHATSAPP
                            <ArrowUpRight size={14} strokeWidth={1.5} />
                        </a>
                    </Reveal>

                    {/* Radno vrijeme — urediti u src/data/site.js (HOURS) */}
                    <Reveal delay={0.24}>
                        <dl className="mt-14 max-w-sm" data-testid="opening-hours">
                            {HOURS.map((h) => (
                                <div
                                    key={h.days}
                                    className="flex items-baseline justify-between border-t border-white/[0.08] py-3.5 text-sm"
                                >
                                    <dt className="text-silver">{h.days}</dt>
                                    <dd
                                        className={
                                            h.time === "Zatvoreno"
                                                ? "text-silver/60"
                                                : "text-bone"
                                        }
                                    >
                                        {h.time}
                                    </dd>
                                </div>
                            ))}
                            <div className="border-t border-white/[0.08]" />
                        </dl>
                    </Reveal>
                </div>

                <Reveal delay={0.15} className="min-h-[360px]">
                    <div className="h-full min-h-[360px] overflow-hidden">
                        <iframe
                            data-testid="google-map-embed"
                            title="Ramljak Auto Detailing Centar — Ljubuški"
                            src={MAP_EMBED}
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            className="h-full w-full"
                            allowFullScreen
                        />
                    </div>
                </Reveal>
            </div>
        </div>
    </section>
);
